/**
 * Effects tuning (`fx.json`): particle caps per quality preset (spec §8) and their split across the eight
 * VFX material pools, how each projectile weapon draws its rounds, which explosion each ship gets and the
 * torpedo model. Validated with Zod at load; the dev panel may mutate the parsed object.
 */
import { z } from 'zod'
import { WEAPON_IDS, type WeaponId } from '../../data/schemas'
import {
  QUALITY_PRESETS,
  VFX_EFFECT_IDS,
  VFX_MATERIAL_KEYS,
  type QualityPreset,
  type VfxEffectId,
  type VfxMaterialKey,
} from '../../render/contracts'
import fxJson from './fx.json'

const materialKey = z.enum(VFX_MATERIAL_KEYS as unknown as [VfxMaterialKey, ...VfxMaterialKey[]])
const effectId = z.enum(VFX_EFFECT_IDS as unknown as [VfxEffectId, ...VfxEffectId[]])
const presetKey = z.enum(QUALITY_PRESETS as unknown as [QualityPreset, ...QualityPreset[]])
const weaponId = z.enum(WEAPON_IDS as unknown as [WeaponId, ...WeaponId[]])
const count = z.number().int().min(1)

const tracerSchema = z.object({
  /** The pool that draws the round (style guide §3.6: the weapon's colour, never the faction's). */
  material: materialKey,
  /** Streak width in metres. */
  width: z.number().positive(),
  /** Streak length = round speed × this (seconds), clipped to the distance flown from the muzzle. */
  streakSeconds: z.number().nonnegative(),
  /** Normalized age at the end of the round's range (0 = full brightness all the way). */
  fade: z.number().min(0).max(1),
})

export const fxConfigSchema = z
  .object({
    /** Total particles (all pools) per quality preset (spec §8: 256 / 1,024 / 4,096). */
    caps: z.record(presetKey, count),
    /** Every pool gets at least this many slots. */
    minPerPool: count,
    /** Share of the cap per pool; normalized, so only the ratios matter. */
    weights: z.record(materialKey, z.number().positive()),
    /** Projectile weapons that draw rounds; a weapon missing here draws none. */
    tracers: z.partialRecord(weaponId, tracerSchema),
    explosions: z.object({
      /** Ship def id → explosion effect; ships not listed use `defaultShip`. */
      ships: z.record(z.string().min(1), effectId),
      defaultShip: effectId,
    }),
    torpedo: z.object({
      /** Asset id of the torpedo model. */
      model: z.string().min(1),
      /** The model socket the smoke trail leaves from. */
      engineSocket: z.string().min(1),
      /** Torpedo views built at init (more are built on demand). */
      prewarm: z.number().int().min(0),
    }),
  })
  .strict()

export type FxConfig = z.infer<typeof fxConfigSchema>
export type TracerConfig = z.infer<typeof tracerSchema>

/** Parses `fx.json`. Throws a ZodError naming the bad field. */
export function loadFxConfig(raw: unknown = fxJson): FxConfig {
  return fxConfigSchema.parse(raw)
}

/**
 * Splits `total` particles across the pools by weight (floor), with at least `minPerPool` each.
 * Runs at init and on a quality change only (allocates the result).
 */
export function poolCapacities(total: number, weights: Readonly<Record<VfxMaterialKey, number>>, minPerPool: number): Record<VfxMaterialKey, number> {
  let sum = 0
  for (const key of VFX_MATERIAL_KEYS) sum += weights[key]
  const out = {} as Record<VfxMaterialKey, number>
  for (const key of VFX_MATERIAL_KEYS) out[key] = Math.max(minPerPool, Math.floor((total * weights[key]) / sum))
  return out
}
